import type { CartItemResponsePostData } from '../services/api/cart'
import { formatPrecioESCustom } from './price-formatter'

// Frontend CartItemType interface
export interface CartItemType {
  id: string
  name: string
  description: string
  price: string
  quantity?: string
  unit?: string
  area?: string
  imageUrl: string
  totalPrice?: string
  pricePerUnit?: string
  fullName: string
  itemType?: string // "design" | "planter" | null
  m2PerBox?: number | null
  boxes?: number | null
  // New fields for dynamic SVG coloring (only for design items)
  svgUrl?: string
  tones?: Array<{ id: string, hex: string, area: string }>
  availability?: string
}

function formatCartQuantityValue(value: number | null | undefined): string | undefined {
  if (value === null || value === undefined) {
    return undefined
  }

  return value.toLocaleString('es-ES', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  })
}

// Utility function to map API response to frontend CartItemType
export function mapApiCartItemToCartItemType(apiItem: CartItemResponsePostData, translations?: {
  productWithoutDescription?: string
  productWithoutName?: string
  thickness?: string
}): CartItemType {
  const itemData = apiItem.item_data || {}
  const reference
    = typeof itemData.reference === 'string' ? itemData.reference : undefined
  const m2PerBoxRaw = Number(itemData.m2_per_box)
  const boxesRaw = Number(itemData.boxes)

  // Get color names from the colors array
  const getColorNames = () => {
    if (!apiItem.colors || apiItem.colors.length === 0)
      return ''
    return apiItem.colors.join('\n')
  }

  // Calculate price using backend data first, then hardcoded fallback
  const getPrice = () => {
    // Use backend unit_price if available
    if (apiItem.unit_price !== undefined) {
      return `${apiItem.unit_price}€`
    }
    // Hardcoded fallback price (since design object is not available)
    return '0€' // Default hardcoded price
  }

  // Calculate total price using backend data first, then calculated, then fallback
  const getTotalPrice = () => {
    // Use backend total_price if available
    if (apiItem.total_price !== undefined) {
      return `${apiItem.total_price.toFixed(2)}€`
    }
    // Calculate from backend unit_price
    if (apiItem.unit_price !== undefined) {
      const total = apiItem.unit_price * apiItem.amount
      return `${total.toFixed(2)}€`
    }
    // Hardcoded fallback calculation (since design object is not available)
    const fallbackTotal = 0 * apiItem.amount // Default 147€ per unit
    return `${fallbackTotal.toFixed(2)}€`
  }

  // Determine if this is a unit-based product (planter, lattice, zellige, barro_alta, muestra) or area-based (design)
  const isUnitBased = [
    'planter',
    'lattice',
    'zellige',
    'muestra',
  ].includes(apiItem.item_type || '')

  // Build description with colors and optionally thickness for designs
  const buildDescription = () => {
    const colorNames = getColorNames()
    // Add thickness line only for design items
    if (!isUnitBased && apiItem.thickness) {
      const thicknessLabel = translations?.thickness || 'Thickness'
      return colorNames
        ? `${colorNames}\n${thicknessLabel}: ${apiItem.thickness} cm`
        : `${thicknessLabel}: ${apiItem.thickness} cm`
    }
    return colorNames
  }

  return {
    id: apiItem.id,
    fullName:
      apiItem.short_name
      || reference
      || translations?.productWithoutName
      || 'Producto sin nombre',
    name:
      apiItem.short_name
      || reference
      || translations?.productWithoutName
      || 'Producto sin nombre', // Use backend field with fallback
    description: buildDescription(),
    price: formatPrecioESCustom(apiItem.unit_price),
    quantity: formatCartQuantityValue(apiItem.amount),
    // Use backend unit/area if provided, otherwise fallback to calculated values
    unit: apiItem.unit || (isUnitBased ? 'ud' : 'm²'),
    area:
      formatCartQuantityValue(apiItem.area)
      || (isUnitBased ? undefined : formatCartQuantityValue(apiItem.amount)),
    imageUrl: apiItem.image_url || '/images/product-tile.png', // Use backend field with fallback
    totalPrice: formatPrecioESCustom(apiItem.total_price) || getTotalPrice(),
    pricePerUnit: getPrice(),
    itemType: apiItem.item_type,
    m2PerBox: Number.isFinite(m2PerBoxRaw) ? m2PerBoxRaw : null,
    boxes: Number.isFinite(boxesRaw) ? boxesRaw : null,
    // New fields for dynamic SVG coloring
    svgUrl: apiItem.svg_url,
    tones: apiItem.tones,
    availability: apiItem.availability || undefined,
  }
}
